import React, { useState, useEffect } from 'react';
import {
  Typography,
  Tabs,
  Tab,
  Container,
  Paper
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchOperatorActivity, fetchWorkcellActivity } from '../api';
import DateSelector from '../components/DateSelector';
import JsonDataViewer from '../components/JsonDataViewer';
import { formatLocalDate } from '../utils/localDateTime';

const ActivityDataPage = () => {
  const { date } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Data states
  const [operatorData, setOperatorData] = useState(null);
  const [workcellData, setWorkcellData] = useState(null);

  // Loading states
  const [operatorLoading, setOperatorLoading] = useState(false);
  const [workcellLoading, setWorkcellLoading] = useState(false);

  // Error states
  const [operatorError, setOperatorError] = useState(null);
  const [workcellError, setWorkcellError] = useState(null);

  // Default to today if no date is provided
  const selectedDate = date || formatLocalDate(new Date());

  // Navigate only if the URL doesn't match
  useEffect(() => {
    if (!date || date !== selectedDate) {
      navigate(`/activity-data/${selectedDate}`, { replace: true });
    }
  }, [date, selectedDate, navigate]);

  // Fetch data from both endpoints
  useEffect(() => {
    const loadOperatorData = async () => {
      try {
        setOperatorLoading(true);
        setOperatorError(null);
        const data = await fetchOperatorActivity(selectedDate);
        setOperatorData(data);
      } catch (error) {
        console.error('Failed to fetch operator activity:', error);
        setOperatorError('Failed to load operator activity data');
      } finally {
        setOperatorLoading(false);
      }
    };

    const loadWorkcellData = async () => {
      try {
        setWorkcellLoading(true);
        setWorkcellError(null);
        const data = await fetchWorkcellActivity(selectedDate);
        setWorkcellData(data);
      } catch (error) {
        console.error('Failed to fetch workcell activity:', error);
        setWorkcellError('Failed to load workcell activity data');
      } finally {
        setWorkcellLoading(false);
      }
    };

    loadOperatorData();
    loadWorkcellData();
  }, [selectedDate]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ mb: 2, mt: 2 }}>
        Raw Activity Data
      </Typography>

      <DateSelector
        date={selectedDate}
        setDate={(newDate) => navigate(`/activity-data/${newDate}`)}
      />

      <Paper sx={{ mt: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Operator Activity" />
          <Tab label="Workcell Activity" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <JsonDataViewer
          title="Operator Activity Data"
          data={operatorData}
          loading={operatorLoading}
          error={operatorError}
        />
      )}

      {activeTab === 1 && (
        <JsonDataViewer
          title="Workcell Activity Data"
          data={workcellData}
          loading={workcellLoading}
          error={workcellError}
        />
      )}
    </Container>
  );
};

export default ActivityDataPage;
